/**
 * Banner Snooze Utilities
 *
 * Utilities for managing dismissal state of reconnection banners with time-to-live (TTL) support.
 * Implements 24-hour snooze period for dismissed banners, with automatic expiry checking.
 *
 * @module bannerSnooze
 */

import type { ProviderKey } from '@/types/sync/ProviderTypes';
import { maskUserId } from './piiMasking';

/**
 * Structure of the snooze record stored in localStorage
 */
export interface BannerSnoozeRecord {
  /** Timestamp (ms since epoch) when the banner was dismissed */
  timestamp: number;
}

/**
 * TTL for banner snooze: 24 hours in milliseconds
 * After this duration, the banner will reappear even if previously dismissed
 */
export const BANNER_SNOOZE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Checks if a reconnection banner has been dismissed (snoozed) for the given user and provider.
 * Returns true if banner was dismissed within the last 24 hours.
 *
 * SSR-safe: Returns false if localStorage is unavailable (server-side rendering or Safari private mode).
 *
 * @param userId - The user ID to check (will be masked for PII protection in storage key)
 * @param providerKey - The provider key (e.g., 'google-drive', 'local')
 * @returns true if banner is currently snoozed, false otherwise
 *
 * @example
 * ```typescript
 * if (!isBannerDismissed(user.id, 'google-drive')) {
 *   // Show banner
 * }
 * ```
 */
export function isBannerDismissed(userId: string, providerKey: ProviderKey): boolean {
  // SSR guard: localStorage unavailable
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const maskedUserId = maskUserId(userId);
    const key = `sync-reconnect-banner-dismissed-${maskedUserId}-${providerKey}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return false;
    }

    // Parse the snooze record
    const record: BannerSnoozeRecord = JSON.parse(stored);

    // Check TTL (24 hours)
    const elapsed = Date.now() - record.timestamp;
    if (elapsed >= BANNER_SNOOZE_TTL_MS) {
      // Expired - clean up
      localStorage.removeItem(key);
      return false;
    }

    return true;
  } catch (_error) {
    // Parse error or localStorage unavailable (Safari private mode, quota exceeded)
    // Clean up corrupt state if we can access localStorage
    try {
      const maskedUserId = maskUserId(userId);
      const key = `sync-reconnect-banner-dismissed-${maskedUserId}-${providerKey}`;
      localStorage.removeItem(key);
    } catch {
      // Ignore cleanup failure
    }
    return false;
  }
}

/**
 * Dismisses (snoozes) a reconnection banner for 24 hours.
 * Records the dismissal timestamp in localStorage with PII-protected key.
 *
 * SSR-safe: No-op if localStorage is unavailable.
 *
 * @param userId - The user ID (will be masked for PII protection in storage key)
 * @param providerKey - The provider key (e.g., 'google-drive', 'local')
 *
 * @example
 * ```typescript
 * // User clicks "Dismiss" button
 * dismissBanner(user.id, 'google-drive');
 * ```
 */
export function dismissBanner(userId: string, providerKey: ProviderKey): void {
  // SSR guard: localStorage unavailable
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const maskedUserId = maskUserId(userId);
    const key = `sync-reconnect-banner-dismissed-${maskedUserId}-${providerKey}`;
    const record: BannerSnoozeRecord = {
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(record));
  } catch (_error) {
    // Silent failure: localStorage unavailable (Safari private mode, quota exceeded)
    // Banner will reappear on next page load, which is acceptable fallback behavior
  }
}

/**
 * Clears the dismissal state for a reconnection banner, allowing it to be shown again.
 * Typically called after successful reconnection.
 *
 * SSR-safe: No-op if localStorage is unavailable.
 *
 * @param userId - The user ID (will be masked for PII protection in storage key)
 * @param providerKey - The provider key (e.g., 'google-drive', 'local')
 *
 * @example
 * ```typescript
 * // After successful reconnection
 * clearBannerDismissal(user.id, 'google-drive');
 * ```
 */
export function clearBannerDismissal(userId: string, providerKey: ProviderKey): void {
  // SSR guard: localStorage unavailable
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const maskedUserId = maskUserId(userId);
    const key = `sync-reconnect-banner-dismissed-${maskedUserId}-${providerKey}`;
    localStorage.removeItem(key);
  } catch (_error) {
    // Silent failure: localStorage unavailable
    // This is acceptable - banner state will reset on next session anyway
  }
}
